import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LogOut,
  AlarmClock,
  BarChart3,
  Check,
  ChevronsUpDown,
  Gauge,
  Inbox,
  Layers,
  Mail,
  Moon,
  Plus,
  RotateCcw,
  Scale,
  Search,
  Settings2,
  ShieldCheck,
  Sun,
  User,
  BookOpen,
  Megaphone,
  Menu as MenuIcon,
  X,
} from 'lucide-react';
import { api, session, type DevUser, type Me } from '../lib/api.js';
import { confirmDiscard } from '../lib/unsaved.js';
import { areaForPath, canAccess, capabilitiesIn, landingFor, ROLE_LABEL, type Area } from '../lib/access.js';
import { NewAssignments } from './NewAssignments.js';
import { useLiveUpdates } from '../lib/useLiveUpdates.js';
import { ReportBug } from './ReportBug.js';
import { isDevAuth, signOut } from '../lib/sso.js';
import type { ModuleKey, TenantRole } from '@esa/shared';
import {
  Avatar,
  Badge,
  Button,
  Kbd,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Tooltip,
  cn,
  useToast,
} from '../ui/index.js';
import { deskOnRoleChange } from '../lib/deskChoice.js';
import { CommandPalette } from './CommandPalette.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { HelpPanel } from './HelpPanel.js';
import { NewGroupDialog } from './NewGroupDialog.js';
import { BulletinBoard, PostBulletin, useBulletins } from './Bulletins.js';

/**
 * The themes, in the order the toggle steps through them. Each is a token block in
 * styles/tokens.css; Glass adds its materials in styles/theme-glass.css.
 */
const THEMES: { value: string; label: string; icon: ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <Sun aria-hidden className="h-4 w-4" /> },
  { value: 'dark', label: 'Dark', icon: <Moon aria-hidden className="h-4 w-4" /> },
  { value: 'glass', label: 'Glass', icon: <Layers aria-hidden className="h-4 w-4" /> },
];

/** The theme after this one, wrapping round. */
function nextTheme(current: string) {
  const i = THEMES.findIndex((t) => t.value === current);
  return THEMES[(i + 1) % THEMES.length]!;
}

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  area: Area;
  /** Key into the queue-counts payload, when the item shows a count. */
  count?: 'open' | 'mine' | 'breached';
  /**
   * The feature this belongs to, when it belongs to one a group can switch
   * off. Absent means the item is part of ticketing itself and is always
   * offered -- Queue and Configuration are not modules.
   */
  module?: ModuleKey;
}

const NAV: NavItem[] = [
  { to: '/queue', label: 'Queue', icon: <Inbox />, area: 'queue', count: 'open' },
  { to: '/dashboard', label: 'Dashboard', icon: <BarChart3 />, area: 'dashboard', module: 'reports' },
  // Same `report.tenant` gate as the dashboard — this is a report, not a
  // separate capability, and the nav item and the page it points to must
  // never disagree about who can open it.
  { to: '/reports/mix', label: 'Desk performance', icon: <Gauge />, area: 'dashboard', module: 'reports' },
  // Same gate again — stalls and automation cases are arithmetic over the
  // same tenant data as the two reports above, not a separate capability.
  // "Daylighting" is the internal name for this and means nothing to a queue
  // lead, so the label says what pressing it does instead.
  {
    to: '/reports/daylight',
    label: 'Stalls & automation',
    icon: <AlarmClock />,
    area: 'dashboard',
    // Its own module, not `reports`: a group can want the dashboard without
    // wanting this, and that is the whole reason modules are per-feature
    // rather than per-capability.
    module: 'daylight',
  },
  { to: '/intake', label: 'Intake', icon: <Mail />, area: 'intake', module: 'intake' },
  { to: '/admin', label: 'Configuration', icon: <Settings2 />, area: 'admin' },
];

const PERSONAL: NavItem[] = [
  { to: '/my', label: 'My requests', icon: <User />, area: 'personal', module: 'portal' },
  { to: '/new', label: 'New request', icon: <Plus />, area: 'personal', module: 'portal' },
];

export function Shell() {
  const nav = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState<string>(() => {
    // ?theme=dark forces the theme for a screenshot or a shared link, without
    // needing to click the toggle first.
    const forced = new URLSearchParams(window.location.search).get('theme');
    if (forced && THEMES.some((t) => t.value === forced)) return forced;
    const saved = session.theme;
    return saved && THEMES.some((t) => t.value === saved) ? saved : 'light';
  });
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Below md the sidebar is a drawer behind a menu button: on a phone it was
  // simply missing, and with it every way to get anywhere.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    session.theme = theme;
  }, [theme]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/v1/me') });
  // Open screens refresh the moment a ticket changes (see useLiveUpdates).
  useLiveUpdates(Boolean(me.data));

  const [tenant, setTenant] = useState<string>(() => session.tenant ?? '');
  useEffect(() => {
    if (tenant || !me.data?.tenants.length) return;
    // Tenants arrive busiest-first, so prefer the first one where this
    // identity is actually staff — that is the group worth opening on.
    const staff = me.data.tenants.find((t) => canAccess(t.role, 'queue'));
    const chosen = staff?.id ?? me.data.tenants[0]!.id;
    setTenant(chosen);
    session.tenant = chosen;
  }, [me.data, tenant]);

  /**
   * MADE STAFF IN ANOTHER DESK: OPEN IT, ONCE. The remembered desk is kept
   * across sessions, so a new agent kept opening the desk they had used as a
   * requester and looked as though the grant had not worked (lib/deskChoice).
   */
  const toast = useToast();
  useEffect(() => {
    if (!me.data?.tenants.length || !tenant) return;
    const { switchTo, staffNow } = deskOnRoleChange({
      before: session.staffDesks,
      desks: me.data.tenants.map((t) => ({ id: t.id, staff: canAccess(t.role, 'queue') })),
      current: tenant,
    });
    session.staffDesks = staffNow;
    if (switchTo) {
      const desk = me.data.tenants.find((t) => t.id === switchTo);
      setTenant(switchTo);
      session.tenant = switchTo;
      toast.success(
        `You're now ${desk?.role ? (ROLE_LABEL[desk.role as TenantRole] ?? desk.role).toLowerCase() : 'staff'} in ${desk?.name ?? switchTo}`,
        'Switched you there. Use the desk menu at the top to change desks.',
      );
    }
  }, [me.data, tenant, toast]);

  const current = me.data?.tenants.find((t) => t.id === tenant);
  const role = current?.role ?? null;

  /**
   * If the page you are on is not available in the group you just switched
   * to, move somewhere that is — rather than leaving a 403 on screen.
   */
  useEffect(() => {
    if (!current) return;
    const area = areaForPath(location.pathname);
    if (area && area !== 'comparison' && !canAccess(role, area, capabilitiesIn(me.data, tenant))) {
      nav(landingFor(role), { replace: true });
    }
  }, [current, role, location.pathname, nav]);

  // The queue encodes its filter as ?view=; read it back so the sidebar can
  // show which one is active. Only meaningful on the queue itself.
  const queueView = location.pathname.startsWith('/queue')
    ? (new URLSearchParams(location.search).get('view') ?? 'open')
    : null;

  // Which features this group has. The same endpoint every page already
  // reads, so this is a cache hit rather than another round trip.
  const cfgQ = useQuery({
    queryKey: ['config', tenant],
    queryFn: () => api.get<{ modules: ModuleKey[] }>(`/api/v1/tenants/${tenant}/config`),
    enabled: Boolean(tenant),
    staleTime: 60_000,
  });

  const counts = useQuery({
    queryKey: ['queue-counts', tenant],
    queryFn: () => api.get<Record<string, number>>(`/api/v1/tenants/${tenant}/queue-counts`),
    enabled: Boolean(tenant) && canAccess(role, 'queue'),
    // Mail already takes its own time to arrive. The poll is the one delay
    // here we control, and every second of it is added to a wait somebody
    // is already sitting through.
    refetchInterval: 30_000,
  });

  /**
   * What this group has, as the server resolved it for this person.
   *
   * Undefined while the config is still loading. Treating that as "nothing"
   * would flash an empty sidebar on every page load, so a missing list means
   * "do not filter yet" -- the server is the one refusing, and it does not
   * depend on this arriving.
   */
  const modules = cfgQ.data?.modules;
  const offers = useCallback(
    (n: NavItem) =>
      canAccess(role, n.area, capabilitiesIn(me.data, tenant)) && (n.module === undefined || !modules || modules.includes(n.module)),
    [role, modules],
  );

  const items = useMemo(() => NAV.filter(offers), [offers]);
  const bulletins = useBulletins(tenant);
  const canPostBulletin = Boolean(tenant && bulletins.data?.canPost);
  const personal = useMemo(() => PERSONAL.filter(offers), [offers]);

  return (
    <div className="app-bg flex h-full flex-col">
      <header className="glass sticky top-0 z-40 flex h-topbar shrink-0 items-center gap-2 border-b border-line px-3">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className="md:hidden"
          onClick={() => setNavOpen((o) => !o)}
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={navOpen}
          aria-controls="app-nav"
        >
          {navOpen ? <X /> : <MenuIcon />}
        </Button>
        <button
          onClick={() => nav(landingFor(role))}
          className="flex items-center gap-2 rounded-md px-1 py-1 text-left"
        >
          <Logo />
          <span className="hidden leading-none sm:block">
            <span className="block text-sm font-semibold tracking-tight text-ink">Tributary</span>
            <span className="block text-3xs uppercase tracking-[0.14em] text-ink-tertiary">
              ESA Service Desk
            </span>
          </span>
        </button>

        <span aria-hidden className="mx-0.5 hidden h-4 w-px bg-line sm:block" />

        <TenantSwitcher
          canCreate={Boolean(me.data?.crossTenant)}
          tenants={me.data?.tenants ?? []}
          value={tenant}
          onChange={(id) => {
            // Unsaved changes belong to the desk being left (lib/unsaved.ts).
            if (!confirmDiscard()) return;
            setTenant(id);
            session.tenant = id;
            // A filter belongs to the group it was chosen in. Queue keys and
            // request types are per-tenant and match nothing elsewhere, and a
            // saved view can be empty in one group and full in another — which
            // is how switching to a group with 187 open tickets showed an
            // empty list and "The router refused to guess".
            //
            // A ticket path carries its own tenant, so switching group there
            // has to leave the ticket rather than silently mismatch it.
            const nextRole = me.data?.tenants.find((t) => t.id === id)?.role ?? null;
            nav(location.pathname.startsWith('/t/') ? landingFor(nextRole) : location.pathname, {
              replace: true,
            });
          }}
        />

        {/* The current role, always visible. Not knowing it is what made the
            permission errors baffling rather than obvious. */}
        {role && (
          <span className="hidden sm:contents">
          <Tooltip content={`Your role in ${current?.name}. Access is granted per group.`}>
            <Badge tone={role === 'requester' ? 'neutral' : 'brand'} icon={<ShieldCheck />}>
              {ROLE_LABEL[role as TenantRole] ?? role}
            </Badge>
          </Tooltip>
          </span>
        )}

        <div className="flex-1" />

        <button
          onClick={() => setPaletteOpen(true)}
          className={cn(
            'hidden h-7 items-center gap-2 rounded-md border border-line bg-surface px-2 sm:flex',
            'text-xs text-ink-tertiary transition-colors hover:bg-surface-hover hover:text-ink-secondary',
            'w-40 lg:w-56',
          )}
        >
          <Search aria-hidden className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 text-left">Search</span>
          <Kbd>⌘K</Kbd>
        </button>

        {/* Beside the help and the theme, not in the sidebar: it has to be
            visible from every screen, because the point of it is the person
            who is not looking at the queue. */}
        <NewAssignments tenant={tenant} enabled={canAccess(role, 'queue')} />

        <Tooltip content="Help — what you can do here">
          <span>
            <HelpPanel role={role} />
          </span>
        </Tooltip>

        {/* Beside the theme toggle, on every screen. The moment somebody
            notices something is wrong is the only moment they will report
            it; anything that costs a navigation collects nothing. */}
        <ReportBug tenant={tenant || null} theme={theme} />

        {/* Whoever may post a bulletin can do it from any screen. */}
        {canPostBulletin && (
          <Tooltip content="Post a bulletin: a notice on every page, and on the desk's emails">
            <span>
              <PostBulletin
                tenant={tenant}
                trigger={
                  <Button variant="ghost" size="sm" iconOnly aria-label="New bulletin">
                    <Megaphone />
                  </Button>
                }
              />
            </span>
          </Tooltip>
        )}

        {/* One button, cycling Light → Dark → Glass: the top bar has no room
            for a picker. The tooltip names the one it goes to next. */}
        <Tooltip content={`${nextTheme(theme).label} theme`}>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={() => setTheme(nextTheme(theme).value)}
            aria-label={`Switch to ${nextTheme(theme).label.toLowerCase()} theme`}
          >
            {THEMES.find((t) => t.value === theme)?.icon ?? THEMES[0]!.icon}
          </Button>
        </Tooltip>

        <IdentityMenu me={me.data} />
      </header>

      <div className="flex min-h-0 flex-1">
        {navOpen && (
          <div aria-hidden className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setNavOpen(false)} />
        )}
        <nav
          id="app-nav"
          className={cn(
            'w-sidebar shrink-0 flex-col overflow-y-auto border-r border-line px-2 py-3 md:flex',
            // Open as a drawer it floats over the page, so it takes the solid
            // floating surface; the sidebar's own fill is see-through in glass.
            navOpen
              ? 'fixed bottom-0 left-0 top-topbar z-50 flex w-72 max-w-[85vw] bg-surface-raised shadow-xl md:static md:w-sidebar md:shadow-none'
              : 'hidden bg-sidebar',
          )}
        >
          {items.length > 0 && (
            <SideGroup>
              {items.map((n) => (
                <SideLink
                  key={n.to}
                  {...n}
                  badge={n.count ? counts.data?.[n.count] : undefined}
                />
              ))}
            </SideGroup>
          )}

          {/* THE ROLE AS WELL AS THE MODULE. Every other item in this nav goes
              through `offers`, which checks both; this group checked only
              whether the module was switched on, so a requester in a group
              with knowledge enabled was offered "Suggestions" and got the
              not-available page on clicking it. The rule this codebase
              follows is that the console never describes what the reader
              cannot do. */}
          {canAccess(role, 'knowledge', capabilitiesIn(me.data, tenant)) && (!modules || modules.includes('knowledge')) && (
            <SideGroup label="Knowledge">
              <SideLink to="/knowledge" label="Suggestions" icon={<BookOpen />} area="knowledge" />
            </SideGroup>
          )}

          {/* A heading with nothing under it reads as a broken list, not as
              an absent feature. Every other group here is already guarded;
              this one was missed, so a group with the requester portal
              switched off showed "PERSONAL" followed by whitespace. */}
          {personal.length > 0 && (
            <SideGroup label="Personal">
              {personal.map((n) => (
                <SideLink key={n.to} {...n} />
              ))}
            </SideGroup>
          )}

          {me.data?.crossTenant && (
            <SideGroup label="Firm-wide">
              <SideLink
                to="/comparison"
                label="Team comparison"
                icon={<Scale />}
                area="comparison"
              />
            </SideGroup>
          )}

          <div className="flex-1" />

          {/* At-a-glance queue health, so the sidebar carries information
              rather than only navigation. */}
          {counts.data && canAccess(role, 'queue') && (
            <div className="mx-1 space-y-0.5 rounded-md border border-line bg-surface p-1.5">
              <p className="px-1.5 pb-0.5 pt-0.5 text-3xs font-semibold uppercase tracking-wider text-ink-tertiary">
                Needs attention
              </p>
              {/* Each one filters the queue rather than only reporting a
                  number. They target the SAME `?view=` the queue's own filter
                  chips use, so the two cannot drift into disagreeing about
                  what "overdue" means. */}
              <MiniStat
                label="Overdue"
                value={counts.data.breached}
                tone="danger"
                view="breached"
                currentView={queueView}
              />
              <MiniStat
                label="At risk"
                value={counts.data.at_risk}
                tone="warn"
                view="at_risk"
                currentView={queueView}
              />
              <MiniStat
                label="Unassigned"
                value={counts.data.unassigned}
                tone="neutral"
                view="unassigned"
                currentView={queueView}
              />
              <MiniStat
                label="Needs routing"
                value={counts.data.needs_routing}
                tone="neutral"
                view="needs_routing"
                currentView={queueView}
              />
            </div>
          )}
        </nav>

        <main className="relative z-10 min-w-0 flex-1 overflow-y-auto">
          {/* Bulletins on every page, the queue included (076): few people
              open the Dashboard, and a warning nobody sees warns nobody. */}
          {tenant && <BulletinBoard tenant={tenant} className="px-4 pt-3 sm:px-6" />}
          <ErrorBoundary key={location.pathname} label={location.pathname}>
            {/*
              PAGE STATE BELONGS TO THE GROUP IT WAS BUILT IN.
              `key={tenant}` remounts the page on a group switch, which is
              the difference between "this list is about ESAtech" and "this
              list is about whatever was on screen a moment ago".

              Without it every piece of page-local state outlived the group:
              an open peek pane kept a ticket number that does not exist in
              the new group and showed not-found; the queue and request-type
              filters kept keys that match nothing there, so a group with 187
              open tickets showed an empty list; and worst, the keyset
              pagination stack kept a cursor from the old group's result set,
              so page two of the new one silently skipped rows with nothing
              on screen to say so.

              Done here rather than as a reset per variable because the next
              piece of state someone adds to a page would have to remember to
              join the list, and would not. The switch handler above already
              drops the query string for the same reason; this covers
              everything the query string does not hold.
            */}
            <Outlet
              key={tenant}
              context={{ tenant, role, tenantName: current?.name, me: me.data }}
            />
          </ErrorBoundary>
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        tenant={tenant}
        isStaff={canAccess(role, 'queue')}
        crossTenant={me.data?.crossTenant ?? false}
      />
    </div>
  );
}

/**
 * One line of the queue-health panel, and a filter.
 *
 * A count that cannot be clicked makes the reader do the translation
 * themselves — see "Breached 41", go to the queue, find the right chip. It is
 * the same query either way, so the number may as well be the control.
 *
 * A zero still navigates. "Show me the breached ones" answered with an empty
 * list is a useful answer, and a control that disables itself at zero moves
 * under the cursor as the counts refresh.
 */
function MiniStat({
  label,
  value,
  tone,
  view,
  currentView,
}: {
  label: string;
  value: number | undefined;
  tone: 'danger' | 'warn' | 'neutral';
  view: string;
  currentView: string | null;
}) {
  const dot = { danger: 'bg-danger', warn: 'bg-warn', neutral: 'bg-ink-tertiary' }[tone];
  const active = currentView === view;
  const empty = (value ?? 0) === 0;

  return (
    <NavLink
      to={`/queue?view=${view}`}
      aria-label={`${label}: ${value ?? 0}. Filter the queue.`}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center justify-between gap-2 rounded-xs px-1.5 py-1 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
        active ? 'bg-brand-tint' : 'hover:bg-surface-hover',
      )}
    >
      <span
        className={cn(
          'flex items-center gap-1.5 text-2xs',
          active ? 'font-medium text-ink' : 'text-ink-secondary',
        )}
      >
        <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', dot, empty && 'opacity-40')} />
        {label}
      </span>
      <span
        className={cn('nums text-xs font-semibold', empty ? 'text-ink-tertiary' : 'text-ink')}
      >
        {value ?? '–'}
      </span>
    </NavLink>
  );
}

function SideGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      {label && (
        <p className="mb-1 px-2.5 text-3xs font-semibold uppercase tracking-wider text-ink-tertiary">
          {label}
        </p>
      )}
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function SideLink({ to, label, icon, badge }: NavItem & { badge?: number }) {
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm',
            'transition-colors duration-fast',
            isActive
              ? 'bg-brand-tint font-medium text-brand'
              : 'text-ink-secondary hover:bg-surface-hover hover:text-ink',
          )
        }
      >
        {({ isActive }) => (
          <>
            <span
              aria-hidden
              className={cn(
                'absolute -left-2 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-brand',
                'transition-opacity duration-fast',
                isActive ? 'opacity-100' : 'opacity-0',
              )}
            />
            <span
              aria-hidden
              className={cn(
                'shrink-0 [&>svg]:h-4 [&>svg]:w-4',
                isActive ? 'text-brand' : 'text-ink-tertiary group-hover:text-ink-secondary',
              )}
            >
              {icon}
            </span>
            <span className="flex-1 truncate">{label}</span>
            {badge !== undefined && badge > 0 && (
              <span
                className={cn(
                  'nums shrink-0 rounded-xs px-1 text-3xs font-semibold',
                  isActive ? 'bg-brand/15 text-brand' : 'bg-surface-active text-ink-tertiary',
                )}
              >
                {badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}

function TenantSwitcher({
  tenants,
  value,
  onChange,
  canCreate,
}: {
  tenants: { id: string; name: string; role: string | null }[];
  value: string;
  onChange: (id: string) => void;
  /** Creating a group is platform_admin only, and the API re-checks it. */
  canCreate: boolean;
}) {
  const current = tenants.find((t) => t.id === value);
  if (tenants.length === 0) return null;

  return (
    <Menu>
      <MenuTrigger asChild>
        <Button variant="ghost" size="sm" className="max-w-[13rem] gap-1.5 px-2">
          <span className="truncate font-medium">{current?.name ?? 'Select group'}</span>
          {/* WHO YOU ARE HERE, without opening the menu: "why am I only a
              requester?" was a question about which desk was open. */}
          {current && (
            <span className="hidden shrink-0 text-2xs text-ink-tertiary sm:inline">
              {current.role ? (ROLE_LABEL[current.role as TenantRole] ?? current.role) : 'requester'}
            </span>
          )}
          <ChevronsUpDown aria-hidden className="h-3 w-3 shrink-0 text-ink-tertiary" />
        </Button>
      </MenuTrigger>
      <MenuContent align="start" className="w-72">
        <MenuLabel>Fulfilment group</MenuLabel>
        {tenants.map((t) => (
          <MenuItem key={t.id} onSelect={() => onChange(t.id)}>
            <span className="flex w-full items-center gap-2">
              <span className="min-w-0 flex-1 truncate">{t.name}</span>
              <Badge tone={canAccess(t.role, 'queue') ? 'brand' : 'neutral'}>
                {t.role ? (ROLE_LABEL[t.role as TenantRole] ?? t.role) : 'none'}
              </Badge>
              {t.id === value && <Check aria-hidden className="h-3.5 w-3.5 shrink-0 text-brand" />}
            </span>
          </MenuItem>
        ))}

        {canCreate && (
          <>
            <MenuSeparator />
            {/* Not a MenuItem: the dialog has to outlive the menu, and a menu
                that closes as its own trigger fires never opens it. */}
            <div className="px-1 py-0.5">
              <NewGroupDialog
                trigger={
                  <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                    <Plus aria-hidden className="h-3.5 w-3.5" />
                    New group…
                  </Button>
                }
              />
            </div>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}

/**
 * Identity menu.
 *
 * Grouped by privilege with the platform admin pinned, plus an explicit way
 * back — clearing the local token makes the app re-pick the highest-privilege
 * identity on reload. Without that, signing in as a requester was a one-way
 * door, which is exactly what happened in testing.
 *
 * This whole control exists only because the build runs the dev auth
 * provider; with Entra configured the endpoint behind it is not mounted.
 */
function IdentityMenu({ me }: { me: Me | undefined }) {
  const users = useQuery({
    queryKey: ['dev-users'],
    queryFn: () => api.get<{ users: DevUser[]; provider: string }>('/api/v1/auth/dev-users'),
    // Not merely harmless under Entra — the route is not mounted, so this
    // would 404 on every page load. Relying on the failure to produce an
    // empty list worked, but left a red line in the console suggesting
    // something was broken.
    enabled: isDevAuth(),
    retry: false,
    staleTime: Infinity,
  });

  const signInAs = (token: string) => {
    session.token = token;
    session.tenant = null;
    window.location.href = '/';
  };

  const reset = () => {
    session.token = null;
    session.tenant = null;
    window.location.href = '/';
  };

  const all = users.data?.users ?? [];
  const rank = (u: DevUser): number => {
    const order = ['platform_admin', 'tenant_admin', 'queue_lead', 'agent'];
    for (let i = 0; i < order.length; i++) {
      if (u.roles.some((r) => r?.endsWith(`:${order[i]}`))) return i;
    }
    return 99;
  };
  const label = (u: DevUser): string => {
    const order: TenantRole[] = ['platform_admin', 'tenant_admin', 'queue_lead', 'agent'];
    for (const o of order) {
      const hit = u.roles.find((r) => r?.endsWith(`:${o}`));
      if (hit) {
        const tenantId = hit.split(':')[0];
        return o === 'platform_admin' ? ROLE_LABEL[o] : `${ROLE_LABEL[o]} · ${tenantId}`;
      }
    }
    return 'Requester';
  };

  const platformAdmins = all.filter((u) => rank(u) === 0);
  const staff = all.filter((u) => rank(u) > 0 && rank(u) < 99).sort((a, b) => rank(a) - rank(b));
  const requesters = all.filter((u) => rank(u) === 99);

  return (
    <Menu>
      <MenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 pl-1 pr-1.5">
          {/* The name reaches the avatar too, so the initials are a
              person's rather than the first two letters of an address. */}
          <Avatar name={me?.name ?? null} email={me?.email ?? null} size="sm" />
          <span className="hidden max-w-[10rem] truncate text-xs lg:block">
            {me?.name ?? me?.email}
          </span>
          <ChevronsUpDown aria-hidden className="h-3 w-3 shrink-0 text-ink-tertiary" />
        </Button>
      </MenuTrigger>

      <MenuContent className="w-80">
        <div className="px-2 py-1.5">
          <div className="flex items-center gap-2">
            <Avatar name={me?.name ?? null} email={me?.email ?? null} size="md" />
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-ink">{me?.name ?? me?.email}</p>
              {/* The address stays, underneath: two people share a name far
                  more often than they share a mailbox. */}
              {me?.name && (
                <p className="truncate text-2xs text-ink-tertiary">{me.email}</p>
              )}
            </div>
          </div>
          <p className="mt-1.5 text-2xs text-ink-tertiary">
            {me?.crossTenant ? 'Platform admin · firm-wide access' : 'Group-scoped access'}
          </p>
        </div>

        <MenuSeparator />
        <MenuItem onSelect={signOut} icon={<LogOut />}>
          Sign out
        </MenuItem>

        {users.data && (
          <>
            <MenuSeparator />
            <MenuItem
              onSelect={reset}
              icon={<RotateCcw />}
              shortcut="reset"
            >
              Back to platform admin
            </MenuItem>

            {platformAdmins.length > 0 && (
              <>
                <MenuLabel>Platform</MenuLabel>
                {platformAdmins.map((u) => (
                  <IdentityRow key={u.oid} u={u} label={label(u)} onSelect={signInAs} />
                ))}
              </>
            )}

            {staff.length > 0 && (
              <>
                <MenuLabel>Desk staff</MenuLabel>
                <div className="max-h-56 overflow-y-auto">
                  {staff.map((u) => (
                    <IdentityRow key={u.oid} u={u} label={label(u)} onSelect={signInAs} />
                  ))}
                </div>
              </>
            )}

            {requesters.length > 0 && (
              <>
                <MenuLabel>Requesters</MenuLabel>
                <div className="max-h-40 overflow-y-auto">
                  {requesters.slice(0, 12).map((u) => (
                    <IdentityRow key={u.oid} u={u} label={label(u)} onSelect={signInAs} />
                  ))}
                </div>
              </>
            )}

            <MenuSeparator />
            <p className="px-2 py-1.5 text-3xs leading-snug text-ink-tertiary">
              Local development identities. With Entra ID configured this list does not exist.
            </p>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}

function IdentityRow({
  u,
  label,
  onSelect,
}: {
  u: DevUser;
  label: string;
  onSelect: (token: string) => void;
}) {
  return (
    <MenuItem
      onSelect={() => onSelect(u.token)}
      icon={<Avatar name={u.name} email={u.email} size="xs" />}
    >
      <span className="flex w-full items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate">{u.name ?? u.email}</span>
        <span className="shrink-0 text-3xs text-ink-tertiary">{label}</span>
      </span>
    </MenuItem>
  );
}

function Logo() {
  // The favicon itself, so there is one drawing of the mark and the tab and
  // the top bar can never disagree. It carries its own tile and hairline.
  return <img src="/favicon.svg" alt="" aria-hidden className="h-7 w-7 shrink-0 rounded-md shadow-xs" />;
}
